/**
 * Long-form article tab controller.
 *
 * Intercepts clicks on the sidebar tab list, fetches the destination page,
 * swaps the content column + <head> metadata, and updates history.
 * Falls back to a full navigation on any failure. Without JS, the sidebar
 * links navigate normally.
 *
 * Spec: docs/superpowers/specs/2026-05-13-long-form-blog-design.md
 */
(function () {
	const sidebar = document.querySelector('.lf-sidebar');
	const content = document.getElementById('lf-content');
	if (!sidebar || !content) return;

	const META_NAMES = [
		'description',
		'twitter:title',
		'twitter:description',
		'twitter:image',
	];
	const META_PROPS = [
		'og:title',
		'og:description',
		'og:url',
		'og:image',
		'article:published_time',
		'article:modified_time',
		'article:author',
		'article:section',
		'article:tag',
	];

	const prefetched = new Set();

	function isSameOrigin(url) {
		try {
			return new URL(url, location.href).origin === location.origin;
		} catch (_) {
			return false;
		}
	}

	function shouldBail(event, link) {
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return true;
		if (event.button !== undefined && event.button !== 0) return true;
		if (link.target === '_blank') return true;
		if (link.hasAttribute('download')) return true;
		if (!isSameOrigin(link.href)) return true;
		if (link.href === location.href) return true;
		return false;
	}

	function fullNavigate(href) {
		window.location.href = href;
	}

	async function fetchAndSwap(href, options) {
		const opts = options || {};
		content.classList.add('is-loading');
		content.setAttribute('aria-busy', 'true');

		try {
			const res = await fetch(href, {
				credentials: 'same-origin',
				headers: { 'X-Requested-With': 'BayalarmLongForm' },
			});
			if (!res.ok) throw new Error('HTTP ' + res.status);
			const html = await res.text();
			const doc = new DOMParser().parseFromString(html, 'text/html');

			const newContent = doc.getElementById('lf-content');
			if (!newContent) throw new Error('No #lf-content in response');

			content.innerHTML = newContent.innerHTML;
			content.dataset.tabSlug = newContent.dataset.tabSlug || '';

			swapHead(doc);
			swapSidebarActive(href);

			if (opts.pushState !== false) {
				history.pushState({ lfTab: href }, '', href);
			}

			scrollToContentTop();

			document.dispatchEvent(
				new CustomEvent('bayalarm:longform:tabchange', {
					detail: { href, tabSlug: content.dataset.tabSlug || '' },
				})
			);

			content.focus({ preventScroll: true });
		} catch (err) {
			fullNavigate(href);
			return;
		} finally {
			content.classList.remove('is-loading');
			content.removeAttribute('aria-busy');
		}
	}

	function swapHead(doc) {
		const newTitle = doc.querySelector('title');
		if (newTitle) document.title = newTitle.textContent;

		const newCanonical = doc.querySelector('link[rel="canonical"]');
		const liveCanonical = document.querySelector('link[rel="canonical"]');
		if (newCanonical && liveCanonical) {
			liveCanonical.setAttribute('href', newCanonical.getAttribute('href'));
		}

		META_NAMES.forEach((name) => {
			const incoming = doc.querySelector('meta[name="' + name + '"]');
			const live = document.querySelector('meta[name="' + name + '"]');
			if (incoming && live) {
				live.setAttribute('content', incoming.getAttribute('content') || '');
			}
		});

		META_PROPS.forEach((prop) => {
			const incoming = doc.querySelector('meta[property="' + prop + '"]');
			const live = document.querySelector('meta[property="' + prop + '"]');
			if (incoming && live) {
				live.setAttribute('content', incoming.getAttribute('content') || '');
			}
		});

		// Yoast schema graph: replace if the new doc has one; skip silently otherwise.
		const incomingSchema = doc.querySelector('script.yoast-schema-graph');
		const liveSchema = document.querySelector('script.yoast-schema-graph');
		if (incomingSchema && liveSchema) {
			liveSchema.textContent = incomingSchema.textContent;
		}
	}

	function swapSidebarActive(href) {
		sidebar.querySelectorAll('a').forEach((a) => {
			const li = a.closest('li');
			if (a.href === href) {
				li && li.classList.add('current_page_item');
				a.setAttribute('aria-current', 'page');
			} else {
				li && li.classList.remove('current_page_item');
				a.removeAttribute('aria-current');
			}
		});
	}

	function scrollToContentTop() {
		content.scrollIntoView({ block: 'start', behavior: 'instant' });
	}

	function prefetch(href) {
		if (prefetched.has(href)) return;
		prefetched.add(href);
		const link = document.createElement('link');
		link.rel = 'prefetch';
		link.href = href;
		document.head.appendChild(link);
	}

	sidebar.addEventListener('click', (event) => {
		const link = event.target.closest('a');
		if (!link || !sidebar.contains(link)) return;
		if (shouldBail(event, link)) return;
		event.preventDefault();
		fetchAndSwap(link.href);
	});

	sidebar.addEventListener('mouseenter', (event) => {
		const link = event.target.closest && event.target.closest('a');
		if (!link || !sidebar.contains(link)) return;
		if (!isSameOrigin(link.href)) return;
		prefetch(link.href);
	}, true);

	sidebar.addEventListener('focusin', (event) => {
		const link = event.target.closest('a');
		if (!link || !sidebar.contains(link)) return;
		if (!isSameOrigin(link.href)) return;
		prefetch(link.href);
	});

	window.addEventListener('popstate', () => {
		fetchAndSwap(location.href, { pushState: false });
	});

	const toggle = sidebar.querySelector('.lf-sidebar-toggle');
	const list = sidebar.querySelector('#lf-sidebar-list');
	const toggleLabel = sidebar.querySelector('.lf-sidebar-toggle-label');

	if (toggle && list) {
		toggle.addEventListener('click', () => {
			const open = toggle.getAttribute('aria-expanded') === 'true';
			toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
		});

		// Auto-collapse on tab change.
		document.addEventListener('bayalarm:longform:tabchange', () => {
			toggle.setAttribute('aria-expanded', 'false');
			if (toggleLabel) {
				const active = sidebar.querySelector('a[aria-current="page"]');
				if (active) toggleLabel.textContent = active.textContent.trim();
			}
		});
	}
})();
